import useAddOneShopRoutingLocationRules from "@babackapp/private-api-client/client/use-add-one-shop-routing-location-rules";
import type { ShopRoutingLocationRuleDto } from "@babackapp/private-api-client/types";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from "@babackapp/ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useCarriersContext } from "../../use-carriers-context";
import {
  createEmptyRoutingCondition,
  getRoutingRuleConditions,
  getRoutingRuleLogicalOperator,
  ROUTING_CONDITION_KEYS,
  toConditionalRules,
  type RoutingCondition,
  type RoutingLogicalOperator,
} from "../lib/routing-location-rules";
import { RoutingConditionRow } from "./routing-condition-row";

type Translate = ReturnType<typeof useTranslation>;

const buildFormSchema = (t: Translate) =>
  z.object({
    conditions: z
      .array(
        z.object({
          key: z.enum(ROUTING_CONDITION_KEYS),
          value: z
            .string()
            .trim()
            .min(1, { message: t("settings.shipping.rules.valueRequired") }),
        }),
      )
      .min(1),
    logicalOperator: z.enum(["AND", "OR"]),
    positiveResult: z
      .string()
      .trim()
      .min(1, { message: t("settings.shipping.rules.destinationRequired") }),
  });

type RoutingLocationRuleFormValues = z.infer<
  ReturnType<typeof buildFormSchema>
>;

interface RoutingLocationRuleFormProps {
  /** Absent en création. */
  routingLocationRule?: ShopRoutingLocationRuleDto;
  onCloseForm: () => void;
}

export function RoutingLocationRuleForm({
  routingLocationRule,
  onCloseForm,
}: RoutingLocationRuleFormProps) {
  const t = useTranslation();
  const { shop, editOneCarrierRutingRule } = useCarriersContext();
  const { mutate: addRoutingLocationRule } = useAddOneShopRoutingLocationRules(
    shop.id,
  );

  const defaultLocation =
    shop.locations.find((location) => location.isDefault) ?? shop.locations[0];

  const firstActiveReasonId = shop.reasons.find((reason) => reason.active)?.id;

  const existingConditions = routingLocationRule
    ? getRoutingRuleConditions(routingLocationRule)
    : [];

  const form = useForm<RoutingLocationRuleFormValues>({
    resolver: zodResolver(buildFormSchema(t)),
    defaultValues: {
      // Une règle laissée inerte par la migration n'a aucune condition : on en
      // amorce une vide pour que le marchand ait quelque chose à remplir.
      conditions: existingConditions.length
        ? existingConditions
        : [createEmptyRoutingCondition(firstActiveReasonId ?? undefined)],
      logicalOperator: routingLocationRule
        ? getRoutingRuleLogicalOperator(routingLocationRule)
        : "AND",
      positiveResult:
        routingLocationRule?.positiveResult ?? defaultLocation?.id ?? "",
    },
  });

  const values = form.watch();

  const onSubmit = useCallback(
    (data: RoutingLocationRuleFormValues) => {
      const conditions = toConditionalRules(
        data.conditions,
        data.logicalOperator,
      );

      if (!routingLocationRule) {
        addRoutingLocationRule({
          conditions,
          positiveResult: data.positiveResult,
        });
        onCloseForm();
        return;
      }

      // Le PATCH remplace la liste complète des règles du shop.
      editOneCarrierRutingRule(
        shop.routingLocationRules.map((rule) =>
          rule.id === routingLocationRule.id
            ? { ...rule, conditions, positiveResult: data.positiveResult }
            : rule,
        ),
      );
      onCloseForm();
    },
    [
      addRoutingLocationRule,
      editOneCarrierRutingRule,
      onCloseForm,
      routingLocationRule,
      shop.routingLocationRules,
    ],
  );

  const handleConditionChange = (index: number, condition: RoutingCondition) => {
    const conditions = [...values.conditions];
    conditions[index] = condition;
    form.setValue("conditions", conditions, { shouldValidate: true });
  };

  const handleAddCondition = () =>
    form.setValue("conditions", [
      ...values.conditions,
      createEmptyRoutingCondition(),
    ]);

  const handleRemoveCondition = (index: number) =>
    form.setValue(
      "conditions",
      values.conditions.filter((_, conditionIndex) => conditionIndex !== index),
    );

  const handleLogicalOperatorChange = (operator: string) =>
    form.setValue("logicalOperator", operator as RoutingLogicalOperator);

  return (
    <Form {...form}>
      <form>
        <div className="space-y-2">
          <div className="text">
            {t("settings.shipping.rules.ifTheReturnRequest")}
          </div>

          {values.conditions.map((condition, index) => (
            <div key={index} className="space-y-2">
              {index === 1 && (
                <Select
                  value={values.logicalOperator}
                  onValueChange={handleLogicalOperatorChange}
                >
                  <SelectTrigger className="w-20 h-7 text-xs font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">
                      {t("misc.and").toUpperCase()}
                    </SelectItem>
                    <SelectItem value="OR">
                      {t("misc.or").toUpperCase()}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
              {index > 1 && (
                <Badge variant="outline" className="text-xs px-2 py-0 h-5">
                  {t(
                    `misc.${values.logicalOperator.toLowerCase() as "and" | "or"}`,
                  ).toUpperCase()}
                </Badge>
              )}

              <RoutingConditionRow
                shop={shop}
                condition={condition}
                onChange={(updatedCondition) =>
                  handleConditionChange(index, updatedCondition)
                }
                onRemove={() => handleRemoveCondition(index)}
                canRemove={values.conditions.length > 1}
              />
            </div>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddCondition}
            className="h-7 px-2 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            {t("settings.shipping.rules.addCondition")}
          </Button>

          <div className="text">{t("settings.shipping.rules.sendItTo")}</div>

          {/*
            La destination peut être vide à l'ouverture : certaines règles
            historiques visent un entrepôt depuis supprimé, donc aucune option ne
            correspond. Sans message d'erreur, le bouton d'enregistrement
            paraîtrait cassé — la validation échoue silencieusement.
          */}
          <FormField
            control={form.control}
            name="positiveResult"
            render={() => (
              <FormItem>
                <FormOptionsSelector
                  label={""}
                  value={values.positiveResult}
                  onChange={(location) =>
                    form.setValue("positiveResult", String(location), {
                      shouldValidate: true,
                    })
                  }
                  options={shop.locations.map((location) => ({
                    key: location.id ?? "",
                    label:
                      location?.address?.companyName ||
                      location?.address?.address1 ||
                      "",
                  }))}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-between mt-5">
          <Button type="button" variant="outline" onClick={onCloseForm}>
            {t("misc.cancel")}
          </Button>
          <Button
            type="submit"
            className="btn btn-primary"
            onClick={form.handleSubmit(onSubmit)}
          >
            {routingLocationRule
              ? t("misc.update")
              : t("settings.shipping.rules.addRule")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
